import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiService } from "../../services/axios";
import { toast } from "react-toastify";
import { uploadImage } from "../../utils/upload";

export const fetchStudents = createAsyncThunk(
  "students/fetchStudents",
  async () => {
    const response = await apiService.get("/user/getDetails");
    return response;
  }
);

export const addStudent = createAsyncThunk(
  "students/addStudent",
  async (studentData) => {
    if (studentData.profile_image instanceof File) {
      toast.info("Uploading profile image...");
      studentData.profile_image = await uploadImage(studentData.profile_image);
    }
    const response = await apiService.post("/user/addUser", studentData);
    return response?.data;
  }
);

export const toggleStudentSwitch = createAsyncThunk(
  "students/toggleSwitch",
  async (data) => {
    const response = await apiService.put("/user/updateUser", {
      userId: data?._id,
      isActive: data?.isActive,
    });
    return response.updateStudent;
  }
);

export const updateStudent = createAsyncThunk(
  "students/updateStudent",
  async ({ userId, ...studentData }) => {
    if (studentData.profile_image instanceof File) {
      toast.info("Uploading profile image...");
      studentData.profile_image = await uploadImage(studentData.profile_image);
    }
    const response = await apiService.put("/user/updateUser", {
      userId,
      ...studentData,
    });
    return response.response;
  }
);

export const deleteStudent = createAsyncThunk(
  "students/deleteStudent",
  async ({ userId, isDelete }) => {
    const response = await apiService.put("/user/delete", { userId, isDelete });
    return { userId, ...response.data };
  }
);

const studentSlice = createSlice({
  name: "students",
  initialState: {
    data: [],
    stats: {
      totalStudents: 0,
      activeStudents: 0,
      inactiveStudents: 0,
      totalexam: 0,
      totalquestion: 0,
    },
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(fetchStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || [];
        state.stats = {
          totalStudents: action.payload.totalStudent || 0,
          activeStudents: action.payload.Active || 0,
          inactiveStudents: action.payload.Inactive || 0,
          totalexam: action.payload.totalexam || 0,
          totalquestion: action.payload.totalquestion || 0,
        };
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to fetch students");
      })

      .addCase(addStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addStudent.fulfilled, (state, action) => {
        state.loading = false;
        const newStudent = action.payload?.userData || action.payload;
        state.data = [...state.data, newStudent];
        state.stats.totalStudents += 1;
        toast.success("Student added successfully!");
      })
      .addCase(addStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to add student");
      })

      .addCase(updateStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStudent.fulfilled, (state, action) => {
        state.loading = false;
        const updatedStudent = action.payload?.response || action.payload;
        state.data = state?.data?.map((student) =>
          student?._id === updatedStudent?._id ? updatedStudent : student
        );
        // Recalculate stats so dashboard cards stay in sync without refetch
        const totals = state.data.reduce(
          (acc, s) => {
            if (!s) return acc;
            acc.total += 1;
            if (s.isActive) acc.active += 1;
            else acc.inactive += 1;
            return acc;
          },
          { total: 0, active: 0, inactive: 0 }
        );
        state.stats.totalStudents = totals.total;
        state.stats.activeStudents = totals.active;
        state.stats.inactiveStudents = totals.inactive;
        toast.success("Student updated successfully!");
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to update student");
      })

      .addCase(deleteStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.loading = false;
        const { userId } = action.payload;
        state.data = state.data.filter((student) => student._id !== userId);
        state.stats.totalStudents -= 1;
        toast.success("Student deleted successfully!");
      })
      .addCase(deleteStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error("Failed to delete student");
      });
  },
});

export default studentSlice.reducer;
